import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Sort, SortDirection } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Subject, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from '../../../auth/services/auth.service';
import { FirmwareUpdateService } from '../../services/firmware-update.service';
import { ReceiversService } from '../../../shared/services/receivers.service';
import {
  FirmwareFile,
  PanelDevice,
  CompanyOption,
  QueueDevice,
  ModalityOption,
  FirmwareVersionOption,
  FirmwareSelection,
} from '../../models/firmware-update.model';
import {
  FirmwareConfirmDialogComponent,
  ConfirmDialogData,
  ModalityChangeDevice,
} from '../firmware-confirm-dialog/firmware-confirm-dialog.component';
import { FirmwareConfigDialogComponent } from '../firmware-config-dialog/firmware-config-dialog.component';

@Component({
  selector: 'app-firmware-update',
  templateUrl: './firmware-update.component.html',
  styleUrls: ['./firmware-update.component.scss'],
})
export class FirmwareUpdateComponent implements OnInit, OnDestroy {
  // Device list
  allDevices: PanelDevice[] = [];
  devices: PanelDevice[] = [];
  companies: CompanyOption[] = [];
  apiKeys: string[] = [];
  receiverOptions: { apiKey: string; name: string }[] = [];
  availableFirmware: FirmwareFile[] = [];

  // Filters
  selectedCompany = '';
  companySearchText = '';
  filteredCompanies: CompanyOption[] = [];
  selectedApiKey = '';
  selectedFirmwareType = '';
  macSearch = '';
  // Status visibility filters
  showUpToDate = true;
  showOutdated = true;
  showUnknown = true;

  // Selection
  selectAll = false;
  selectedMacs = new Set<string>();

  // Progress-table (queue) selection — kept separate from the Devices-tab
  // selectedMacs so starting a new update doesn't interfere with an
  // in-progress cancel selection, and vice-versa.
  queueSelectAll = false;
  selectedQueueMacs = new Set<string>();

  // State
  isLoading = false;
  isUpdating = false;
  currentView: 'devices' | 'progress' | 'history' = 'devices';
  pollingStoppedInactivity = false;

  // Global queue (replaces activeJob)
  queueDevices: QueueDevice[] = [];

  // History
  historyDevices: any[] = [];
  filteredHistoryDevices: any[] = [];
  historyColumns: string[] = ['mac', 'company', 'status', 'firmwareVersion', 'modality', 'error', 'updatedAt', 'actions'];
  isLoadingHistory = false;
  historyCompanySearch = '';
  historyCompanySort: SortDirection = '';

  // Pagination
  totalDevices = 0;
  currentPage = 0;
  pageSize = 25;

  // Progress table columns
  displayedColumns: string[] = ['select', 'mac', 'receiver', 'status', 'progress', 'error', 'actions'];

  // MAC search debounce
  private macSearchSubject = new Subject<string>();
  private pollingInterval: any;
  private pollingStartedAt: number | null = null;
  private readonly POLLING_INTERVAL_MS = 3000;
  private readonly POLLING_MAX_DURATION_MS = 10 * 60 * 1000; // 10 minutes

  constructor(
    public authService: AuthService,
    private firmwareService: FirmwareUpdateService,
    private receiversService: ReceiversService,
    private router: Router,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    if (!this.authService.hasRolesAdminOrDistributor()) {
      this.router.navigate(['/maps']);
      return;
    }

    this.loadCompanies();
    this.loadAvailableFirmware();
    this.initializeQueue();

    // Ensure receivers are loaded (they may not be if user navigates directly here)
    if (!this.receiversService.receivers || this.receiversService.receivers?.length === 0) {
      this.receiversService.getReceivers();
    }

    this.macSearchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.currentPage = 0;
        this.loadDevices();
      });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.macSearchSubject.complete();
  }

  // --- Data Loading ---

  loadCompanies(): void {
    // Use companies already available from the user session (loaded at login)
    const userCompanies = this.authService.user?.companies || [];
    this.companies = userCompanies.map((c: any) => ({ _id: c._id, name: c.name }));
    this.filteredCompanies = [...this.companies];
  }

  loadAvailableFirmware(): void {
    this.firmwareService.getAvailableFirmware().subscribe({
      next: (res) => {
        if (res.success) {
          this.availableFirmware = res.files;
        }
      },
      error: () => { },
    });
  }

  /**
   * Cada visita a la página se considera el inicio de una nueva "sesión" de
   * seguimiento: se reconoce (acknowledge) cualquier lote ya finalizado que
   * haya quedado de una visita anterior, y recién después se pide la cola.
   * Así, al entrar sin nada realmente en curso, Progreso queda vacío/oculto
   * (CI-2974) sin depender de detectar cuándo el usuario "se fue" la vez
   * pasada (recarga de página, cierre de pestaña, etc. no disparan ningún
   * hook confiable para eso).
   */
  private initializeQueue(): void {
    this.firmwareService.acknowledgeGlobalQueue().subscribe({
      next: () => this.loadGlobalQueue(),
      error: () => this.loadGlobalQueue(),
    });
  }

  /**
   * Load the global queue — replaces the old loadActiveJobs().
   * If there are active/waiting devices, show the progress view and start polling.
   */
  loadGlobalQueue(): void {
    this.firmwareService.getGlobalQueue().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.queueDevices = res.data;
          const hasActiveDevices = this.queueDevices.length > 0 && this.isQueueRunning;
          if (hasActiveDevices && this.isUpdating) {
            this.startPolling();
          }
        }
      },
      error: () => { },
    });
  }

  loadHistory(): void {
    this.isLoadingHistory = true;
    this.firmwareService.getRecentHistory(15).subscribe({
      next: (res) => {
        this.isLoadingHistory = false;
        if (res.success) {
          this.historyDevices = res.data;
          this.applyHistoryFilters();
        }
      },
      error: () => {
        this.isLoadingHistory = false;
      },
    });
  }

  onHistoryCompanySearch(): void {
    this.applyHistoryFilters();
  }

  clearHistoryCompanySearch(): void {
    this.historyCompanySearch = '';
    this.applyHistoryFilters();
  }

  onHistorySortChange(sort: Sort): void {
    this.historyCompanySort = sort.active === 'company' ? sort.direction : '';
    this.applyHistoryFilters();
  }

  private applyHistoryFilters(): void {
    const search = this.historyCompanySearch.trim().toLowerCase();
    const filtered = search
      ? this.historyDevices.filter((d) => (d.companyName || '').toLowerCase().includes(search))
      : [...this.historyDevices];

    if (this.historyCompanySort) {
      const order = this.historyCompanySort === 'asc' ? 1 : -1;
      filtered.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '') * order);
    }

    this.filteredHistoryDevices = filtered;
  }

  switchView(view: 'devices' | 'progress' | 'history'): void {
    this.currentView = view;
    if (view === 'history' && this.historyDevices.length === 0) {
      this.loadHistory();
    }
    // Volver a "Progreso" cuenta como que el operador está de nuevo mirando
    // la pantalla: si el polling se había cortado por inactividad, se retoma.
    if (view === 'progress') {
      this.resumePollingIfInactive();
    }
  }

  /** Refresca ahora mismo y reactiva el polling si la cola sigue corriendo. */
  resumePollingIfInactive(): void {
    if (this.pollingInterval || !this.isQueueRunning) return;
    this.pollingStoppedInactivity = false;
    this.loadGlobalQueue();
  }

  openConfigDialog(): void {
    this.dialog.open(FirmwareConfigDialogComponent, {
      width: '680px',
      data: { companies: this.companies },
    });
  }

  loadDevices(): void {
    if (!this.selectedCompany) {
      this.allDevices = [];
      this.devices = [];
      this.totalDevices = 0;
      return;
    }

    this.isLoading = true;
    this.firmwareService
      .getDevices({
        company: this.selectedCompany,
        apiKey: this.selectedApiKey || undefined,
        mac: this.macSearch || undefined,
        page: 1,
        limit: 10000,
      })
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res.success) {
            this.allDevices = res.data.map((d) => ({
              ...d,
              selected: this.selectedMacs.has(d.mac),
              updateStatus: this.getUpdateStatus(d),
            }));
            this.applyClientFilters();
            this.loadDeviceOptions();
          }
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  /**
   * Carga, por cada panel listado, las modalidades/versiones permitidas
   * (CI-2800) y preselecciona "Actualizar versión" con la versión ya
   * instalada por defecto.
   */
  loadDeviceOptions(): void {
    const macs = this.allDevices.map((d) => d.mac);
    if (macs.length === 0) return;

    this.firmwareService.getDeviceOptions(macs).subscribe({
      next: (res) => {
        if (!res.success) return;
        const optionsByMac = new Map(res.data.map((o) => [o.mac, o]));
        this.allDevices.forEach((device) => {
          device.deviceOptions = optionsByMac.get(device.mac);
          this.initializeDeviceSelection(device);
        });
      },
      error: () => { },
    });
  }

  private initializeDeviceSelection(device: PanelDevice): void {
    if (!device.deviceOptions || device.deviceOptions.blocked) return;

    const sameModality = device.deviceOptions.modalities.find((m) => m.key === 'same');
    device.selectedModalityKey = 'same';
    const currentVersionOption = sameModality?.versions.find((v) => v.isCurrent);
    device.selectedVersion = currentVersionOption?.version || sameModality?.versions[0]?.version || '';
  }

  onModalityChange(device: PanelDevice): void {
    const modality = this.getSelectedModality(device);
    device.selectedVersion = modality?.versions.find((v) => v.isCurrent)?.version
      || modality?.versions[0]?.version
      || '';
  }

  getModalities(device: PanelDevice): ModalityOption[] {
    return device.deviceOptions?.modalities || [];
  }

  getSelectedModality(device: PanelDevice): ModalityOption | undefined {
    return device.deviceOptions?.modalities.find((m) => m.key === device.selectedModalityKey);
  }

  getSelectedModalityVersions(device: PanelDevice): FirmwareVersionOption[] {
    return this.getSelectedModality(device)?.versions || [];
  }

  /** Con una sola versión disponible el select no aporta nada; se muestra como texto fijo. */
  hasMultipleVersions(device: PanelDevice): boolean {
    return this.getSelectedModalityVersions(device).length > 1;
  }

  hasModalityWarning(device: PanelDevice): boolean {
    return !!this.getSelectedModality(device)?.warning;
  }

  isRowBlocked(device: PanelDevice): boolean {
    return !!device.deviceOptions?.blocked;
  }

  isRowReady(device: PanelDevice): boolean {
    return !!device.deviceOptions;
  }

  applyClientFilters(): void {
    let filtered = this.allDevices;

    // Status filter
    if (!this.showUpToDate) filtered = filtered.filter((d) => d.updateStatus !== 'up_to_date');
    if (!this.showOutdated) filtered = filtered.filter((d) => d.updateStatus !== 'outdated');
    if (!this.showUnknown) filtered = filtered.filter((d) => d.updateStatus !== 'unknown');

    // Firmware type filter
    if (this.selectedFirmwareType) {
      filtered = filtered.filter((d) => d.firmwareType === this.selectedFirmwareType);
    }

    // Separate selected (from any source) and non-selected
    const selectedFromAll = this.allDevices.filter((d) => d.selected && !filtered.includes(d));
    const selectedInFiltered = filtered.filter((d) => d.selected);
    const unselectedInFiltered = filtered.filter((d) => !d.selected);

    // Selected always on top, then the rest
    const combined = [...selectedFromAll, ...selectedInFiltered, ...unselectedInFiltered];
    const totalSelected = selectedFromAll.length + selectedInFiltered.length;

    this.totalDevices = combined.length;

    // Paginate
    const start = this.currentPage * this.pageSize;
    this.devices = combined.slice(start, start + this.pageSize);
    this.selectAll = this.devices.length > 0 && this.devices.every((d) => d.selected);
  }

  // --- Filter Handlers ---

  onCompanySearch(text: string): void {
    const search = (text || '').toLowerCase();
    this.filteredCompanies = this.companies.filter((c) =>
      c.name.toLowerCase().includes(search)
    );
  }

  onCompanySelected(event: any): void {
    this.selectedCompany = event.option.value;
    this.onCompanyChange();
  }

  onCompanyBlur(): void {
    // If the text doesn't match any company, reset
    setTimeout(() => {
      if (this.selectedCompany) {
        const match = this.companies.find((c) => c._id === this.selectedCompany);
        if (match) {
          this.companySearchText = match.name;
        }
      } else {
        const textMatch = this.companies.find(
          (c) => c.name.toLowerCase() === (this.companySearchText || '').toLowerCase()
        );
        if (!textMatch) {
          this.companySearchText = '';
        }
      }
    }, 200);
  }

  clearCompany(): void {
    this.companySearchText = '';
    this.selectedCompany = '';
    this.filteredCompanies = [...this.companies];
    this.onCompanyChange();
  }

  displayCompanyName(companyId: string): string {
    if (!companyId) return '';
    const match = this.companies.find((c) => c._id === companyId);
    return match ? match.name : companyId;
  }

  onCompanyChange(): void {
    this.selectedApiKey = '';
    this.selectedFirmwareType = '';
    this.currentPage = 0;
    this.selectedMacs.clear();
    this.loadApiKeys();
    this.loadDevices();
  }

  loadApiKeys(): void {
    if (!this.selectedCompany) {
      this.apiKeys = [];
      this.receiverOptions = [];
      return;
    }
    // Use receivers already loaded in ReceiversService, filtered by company
    const receivers = this.receiversService.receivers || [];
    const companyReceivers = receivers.filter((r) => r.company === this.selectedCompany);

    // Build unique receiver options with name
    const seen = new Set<string>();
    this.receiverOptions = [];
    for (const r of companyReceivers) {
      if (r.apiKey && !seen.has(r.apiKey)) {
        seen.add(r.apiKey);
        this.receiverOptions.push({ apiKey: r.apiKey, name: r.name || r.apiKey });
      }
    }
    this.receiverOptions.sort((a, b) => a.name.localeCompare(b.name));
    this.apiKeys = this.receiverOptions.map((r) => r.apiKey);
  }

  onApiKeyChange(): void {
    this.currentPage = 0;
    this.loadDevices();
  }

  onMacSearch(): void {
    this.macSearchSubject.next(this.macSearch);
  }

  onStatusFilterChange(): void {
    this.currentPage = 0;
    this.selectAll = false;
    this.applyClientFilters();
  }

  get filteredDevices(): PanelDevice[] {
    return this.devices;
  }

  get firmwareTypes(): string[] {
    const types = new Set(this.allDevices.map((d) => d.firmwareType).filter(Boolean));
    return [...types].sort();
  }

  get uniqueFirmwareVersions(): { firmwareType: string; version: string }[] {
    const seen = new Set<string>();
    const result: { firmwareType: string; version: string }[] = [];
    for (const fw of this.availableFirmware || []) {
      const key = `${fw.firmwareType}|${fw.version}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ firmwareType: fw.firmwareType, version: fw.version });
      }
    }
    return result;
  }

  getReceiverName(apiKey: string): string {
    const match = this.receiverOptions.find((r) => r.apiKey === apiKey);
    if (match) return match.name;
    // Fallback to receiversService
    const receiver = (this.receiversService.receivers || []).find((r) => r.apiKey === apiKey);
    return receiver?.name || apiKey;
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.applyClientFilters();
  }

  // --- Selection ---

  toggleSelectAll(): void {
    this.filteredDevices.forEach((d) => {
      if (this.isRowBlocked(d)) return;
      d.selected = this.selectAll;
      if (this.selectAll) {
        this.selectedMacs.add(d.mac);
      } else {
        this.selectedMacs.delete(d.mac);
      }
    });
  }

  toggleDevice(device: PanelDevice): void {
    if (device.selected) {
      this.selectedMacs.add(device.mac);
    } else {
      this.selectedMacs.delete(device.mac);
    }
    this.selectAll = this.filteredDevices.length > 0 && this.filteredDevices.every((d) => d.selected);
  }

  getSelectedDevices(): PanelDevice[] {
    return this.allDevices.filter((d) => d.selected);
  }

  getSelectedCount(): number {
    return this.selectedMacs.size;
  }

  // --- Firmware Version Comparison ---

  getUpdateStatus(device: PanelDevice): 'up_to_date' | 'outdated' | 'unknown' {
    if (!device.firmVersion || !this.availableFirmware || this.availableFirmware.length === 0) {
      return 'unknown';
    }
    const matchingFirmware = this.availableFirmware.find(
      (f) => f.firmwareType === device.firmwareType
    );
    if (!matchingFirmware) return 'unknown';

    const deviceVersion = this.normalizeVersion(device.firmVersion);
    const availableVersion = this.normalizeVersion(matchingFirmware.version);

    if (deviceVersion === availableVersion) return 'up_to_date';
    if (deviceVersion < availableVersion) return 'outdated';
    return 'up_to_date'; // newer is also considered up_to_date
  }

  private normalizeVersion(version: string): string {
    return version.replace(/^v/i, '').trim();
  }

  // --- Update Logic ---

  startUpdate(): void {
    const selected = this.getSelectedDevices();
    if (selected?.length === 0) {
      this.snackBar.open(
        this.translate.instant('FirmwareUpdate.NoMacsProvided'),
        this.translate.instant('Shared.Accept'),
        { duration: 3000 }
      );
      return;
    }

    const modalityChangeDevices: ModalityChangeDevice[] = [];
    const reinstallDevices: { mac: string; currentVersion: string; targetVersion: string }[] = [];
    const downgradeDevices: { mac: string; currentVersion: string; targetVersion: string }[] = [];

    selected.forEach((device) => {
      const modality = this.getSelectedModality(device);
      if (!modality || !device.selectedVersion) return;

      if (device.selectedModalityKey !== 'same') {
        const currentModality = device.deviceOptions?.modalities.find((m) => m.key === 'same');
        modalityChangeDevices.push({
          mac: device.mac,
          currentVersion: device.firmVersion,
          currentLabel: currentModality?.label || device.firmwareType,
          targetVersion: device.selectedVersion,
          targetLabel: modality.label,
          warning: modality.warning,
        });
        return;
      }

      if (!device.firmVersion) return;
      const current = this.normalizeVersion(device.firmVersion);
      const target = this.normalizeVersion(device.selectedVersion);

      if (current === target) {
        reinstallDevices.push({ mac: device.mac, currentVersion: current, targetVersion: target });
      } else if (current > target) {
        downgradeDevices.push({ mac: device.mac, currentVersion: current, targetVersion: target });
      }
    });

    // Un cambio de modalidad interrumpe la acción y se confirma primero; los
    // chequeos de reinstalación/downgrade (dentro de la misma rama) se
    // evalúan recién después de esa confirmación.
    if (modalityChangeDevices.length > 0) {
      const dialogRef = this.dialog.open(FirmwareConfirmDialogComponent, {
        width: '520px',
        data: { type: 'modality-change', devices: modalityChangeDevices } as ConfirmDialogData,
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.continueAfterModalityConfirm(selected, downgradeDevices, reinstallDevices);
        }
      });
      return;
    }

    this.continueAfterModalityConfirm(selected, downgradeDevices, reinstallDevices);
  }

  private continueAfterModalityConfirm(
    selected: PanelDevice[],
    downgradeDevices: { mac: string; currentVersion: string; targetVersion: string }[],
    reinstallDevices: { mac: string; currentVersion: string; targetVersion: string }[]
  ): void {
    if (downgradeDevices.length > 0) {
      this.showConfirmDialog('downgrade', downgradeDevices, selected);
      return;
    }

    if (reinstallDevices.length > 0 && reinstallDevices.length === selected.length) {
      this.showConfirmDialog('reinstall', reinstallDevices, selected);
      return;
    }

    this.executeUpdate(selected);
  }

  private showConfirmDialog(
    type: 'reinstall' | 'downgrade',
    devices: { mac: string; currentVersion: string; targetVersion: string }[],
    allSelected: PanelDevice[]
  ): void {
    const dialogRef = this.dialog.open(FirmwareConfirmDialogComponent, {
      width: '500px',
      data: { type, devices } as ConfirmDialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.executeUpdate(allSelected, true);
      }
    });
  }

  private executeUpdate(selected: PanelDevice[], force: boolean = false): void {
    this.isLoading = true;
    const selections: FirmwareSelection[] = selected
      .filter((d) => d.selectedVersion && this.getSelectedModality(d))
      .map((d) => ({
        mac: d.mac,
        firmwareType: this.getSelectedModality(d)!.firmwareType,
        version: d.selectedVersion as string,
      }));

    this.firmwareService.startUpdate(selections, force).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success && res.batchId) {
          this.isUpdating = true;
          this.currentView = 'progress';
          this.stopPolling();
          this.loadGlobalQueue();
          this.startPolling();
          // Un lote puede quedar parcialmente encolado: algunas selecciones
          // pueden ser rechazadas por el backend (revalidación server-side)
          // sin abortar el resto — hay que avisarlo, no queda implícito.
          const message = res.rejected?.length
            ? this.translate.instant('FirmwareUpdate.UpdateStartedPartial', { rejected: res.rejected.length })
            : this.translate.instant('FirmwareUpdate.UpdateStarted');
          this.snackBar.open(message, this.translate.instant('Shared.Accept'), { duration: 5000 });
        } else {
          this.snackBar.open(
            res.reason || this.translate.instant('FirmwareUpdate.UpdateFailed'),
            this.translate.instant('Shared.Accept'),
            { duration: 5000 }
          );
        }
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open(
          this.translate.instant('FirmwareUpdate.UpdateFailed'),
          this.translate.instant('Shared.Accept'),
          { duration: 5000 }
        );
      },
    });
  }

  toggleView(): void {
    this.currentView = this.currentView === 'devices' ? 'progress' : 'devices';
  }

  // --- Queue helpers ---

  /** True if any device in the queue is not in a terminal state */
  get isQueueRunning(): boolean {
    return this.queueDevices.some((d) =>
      !['completed', 'failed', 'rejected'].includes(d.status)
    );
  }

  /** Overall queue status for display */
  get queueStatus(): string {
    if (this.queueDevices.length === 0) return 'empty';
    if (this.isQueueRunning) return 'running';
    const allSuccess = this.queueDevices.every((d) => d.status === 'completed');
    return allSuccess ? 'completed' : 'completed_with_errors';
  }

  // --- Polling ---

  private startPolling(): void {
    if (this.pollingInterval) return;
    this.pollingStartedAt = Date.now();
    this.pollingStoppedInactivity = false;
    this.pollingInterval = setInterval(() => {
      const exceededMaxDuration = Date.now() - this.pollingStartedAt > this.POLLING_MAX_DURATION_MS;
      if (exceededMaxDuration) {
        this.stopPolling();
        this.pollingStoppedInactivity = true;
        return;
      }
      this.firmwareService.getGlobalQueue().subscribe({
        next: (res) => {
          if (res.success && res.data) {
            this.queueDevices = res.data;
            const queueFinished = this.queueDevices.length === 0 || !this.isQueueRunning;
            if (queueFinished) {
              this.stopPolling();
            }
          }
        },
        error: () => { },
      });
    }, this.POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      this.pollingStartedAt = null;
    }
  }

  // --- Job Helpers ---

  /** Only devices not yet in a terminal state can be cancelled — mirrors the backend's cancelDevices() check. */
  isCancelable(device: QueueDevice): boolean {
    return !['completed', 'failed', 'rejected'].includes(device.status);
  }

  toggleQueueDevice(device: QueueDevice): void {
    if (this.selectedQueueMacs.has(device.mac)) {
      this.selectedQueueMacs.delete(device.mac);
    } else {
      this.selectedQueueMacs.add(device.mac);
    }
    const cancelableDevices = this.queueDevices.filter((d) => this.isCancelable(d));
    this.queueSelectAll =
      cancelableDevices.length > 0 && cancelableDevices.every((d) => this.selectedQueueMacs.has(d.mac));
  }

  toggleAllQueueDevices(): void {
    this.queueSelectAll = !this.queueSelectAll;
    this.queueDevices.filter((d) => this.isCancelable(d)).forEach((d) => {
      if (this.queueSelectAll) this.selectedQueueMacs.add(d.mac);
      else this.selectedQueueMacs.delete(d.mac);
    });
  }

  hasQueueSelection(): boolean {
    return this.selectedQueueMacs.size > 0;
  }

  cancelUpdate(): void {
    const macs = [...this.selectedQueueMacs];
    this.firmwareService.cancelDevices(macs).subscribe({
      next: () => {
        this.selectedQueueMacs.clear();
        this.queueSelectAll = false;
        this.snackBar.open(
          this.translate.instant('FirmwareUpdate.UpdateCancelled'),
          this.translate.instant('Shared.Accept'),
          { duration: 3000 }
        );
      },
    });
  }

  retryDevice(device: any): void {
    this.firmwareService.retryDevice(device.mac).subscribe({
      next: (res) => {
        if (res.success) {
          device.status = 'waiting';
          device.progress = 0;
          device.error = undefined;
          device.rejectionReason = undefined;

          // Refresh the queue
          this.loadGlobalQueue();
          this.isUpdating = true;
          this.currentView = 'progress';
          this.startPolling();

          this.snackBar.open(
            this.translate.instant('FirmwareUpdate.DeviceRequeued', { mac: device.mac }),
            this.translate.instant('Shared.Accept'),
            { duration: 3000 }
          );
        } else {
          this.snackBar.open(
            res.reason || 'Error al reintentar',
            this.translate.instant('Shared.Accept'),
            { duration: 5000 }
          );
        }
      },
      error: () => {
        this.snackBar.open(
          'Error al reintentar la actualización',
          this.translate.instant('Shared.Accept'),
          { duration: 5000 }
        );
      },
    });
  }

  isRetriable(device: any): boolean {
    return device.status === 'failed' || device.status === 'rejected';
  }

  getStatusLabel(device: any): string {
    const key = `FirmwareUpdate.Status.${device.status}`;
    return this.translate.instant(key);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'failed':
        return 'status-failed';
      case 'rejected':
        return 'status-rejected';
      case 'queued':
      case 'waiting':
        return 'status-queued';
      default:
        return 'status-active';
    }
  }

  isJobFinished(): boolean {
    return this.queueDevices.length > 0 && !this.isQueueRunning;
  }

  // --- Counter Methods (using queueDevices) ---

  getActiveCount(): number {
    return this.queueDevices.filter((d) =>
      !['waiting', 'completed', 'failed', 'rejected'].includes(d.status)
    ).length;
  }

  getWaitingCount(): number {
    return this.queueDevices.filter((d) => d.status === 'waiting').length;
  }

  getCompletedCount(): number {
    return this.queueDevices.filter((d) => d.status === 'completed').length;
  }

  getFailedCount(): number {
    return this.queueDevices.filter((d) => d.status === 'failed').length;
  }

  getRejectedCount(): number {
    return this.queueDevices.filter((d) => d.status === 'rejected').length;
  }

  getTotalCount(): number {
    return this.queueDevices.length;
  }

  getHistoryCountByStatus(status: string): number {
    return this.filteredHistoryDevices.filter((d) => d.status === status).length;
  }

  hasFailedDevices(): boolean {
    return this.queueDevices.some((d) => d.status === 'failed');
  }

  retryFailedDevices(): void {
    const failedDevices = this.queueDevices.filter((d) => d.status === 'failed');

    if (failedDevices.length === 0) return;

    // Same per-device retry as the single "Reintentar" action (retryDevice),
    // just fanned out over every failed device instead of one at a time.
    this.isLoading = true;
    forkJoin(failedDevices.map((device) => this.firmwareService.retryDevice(device.mac))).subscribe({
      next: (results) => {
        this.isLoading = false;
        const succeeded = results.filter((res) => res.success).length;

        results.forEach((res, i) => {
          if (!res.success) return;
          const device = failedDevices[i];
          device.status = 'waiting';
          device.progress = 0;
          device.error = undefined;
          device.rejectionReason = undefined;
        });

        if (succeeded > 0) {
          this.loadGlobalQueue();
          this.isUpdating = true;
          this.currentView = 'progress';
          this.startPolling();
        }

        this.snackBar.open(
          succeeded === failedDevices.length
            ? this.translate.instant('FirmwareUpdate.RetryStarted', { count: succeeded })
            : this.translate.instant('FirmwareUpdate.RetryPartial', { succeeded, total: failedDevices.length }),
          this.translate.instant('Shared.Accept'),
          { duration: 5000 }
        );
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open(
          this.translate.instant('FirmwareUpdate.UpdateFailed'),
          this.translate.instant('Shared.Accept'),
          { duration: 5000 }
        );
      },
    });
  }

  resetView(): void {
    this.firmwareService.acknowledgeGlobalQueue().subscribe({ error: () => { } });
    this.queueDevices = [];
    this.isUpdating = false;
    this.currentView = 'devices';
    this.selectedMacs.clear();
    this.selectedQueueMacs.clear();
    this.queueSelectAll = false;
    this.stopPolling();
    this.loadDevices();
  }

  formatMac(mac: string): string {
    return mac ? mac.toUpperCase() : '';
  }
}
